import { Injectable } from "@angular/core";
import { v4 as uuid } from "uuid";
import { environment } from "../../../environments/environment";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable, BehaviorSubject, defer, from, of, throwError, forkJoin, Subject } from "rxjs";
import {
  take,
  filter, 
  switchMap,  
  map,
  catchError,
  concatMap,
  delay,
  retryWhen,
  takeUntil,
  tap,
  shareReplay,
  finalize,
} from "rxjs/operators";

import { Router } from "@angular/router";
import { AuthRefreshService } from "../../../app/services/auth-refresh.service";
import { AuthTokenStoreService } from "../../../app/services/auth-token-store.service";
import { SecureStorageService } from "../../../app/services/secure-storage.service";
import { AdminAuthService } from "../../admin/services/admin-auth.service";

// Stable stringify utility to build request keys without throwing on cycles
// Recursively sorts object keys so that nested payload differences (e.g., searchTerm)
// affect the cache key. The previous implementation only sorted top-level keys via
// JSON.stringify's replacer array, which unintentionally dropped nested keys from
// the stringified output and caused all requests with the same top-level shape
// to share a single key.
function safeStableStringify(value: any): string {
  const seen = new WeakSet();

  const sortDeep = (val: any): any => {
    if (val === null || typeof val !== 'object') return val;
    if (seen.has(val)) return '[Circular]';
    seen.add(val);

    if (Array.isArray(val)) {
      // Preserve array order but process elements recursively
      return val.map(sortDeep);
    }

    const sorted: any = {};
    for (const key of Object.keys(val).sort()) {
      sorted[key] = sortDeep(val[key]);
    }
    return sorted;
  };

  try {
    return JSON.stringify(sortDeep(value));
  } catch {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
}

@Injectable()
export class ApiRequestService {
  urlEnvironment = "";
  demoUrlEnvironment = "";
  company = "";
  private cancelRequest$: Subject<void> = new Subject<void>();
  private readonly maxRetries = 5; // Maximum number of retries

  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  private readonly refreshFailedToken = "__REFRESH_FAILED__";

  // Track in-flight requests to avoid duplicate HTTP calls (by URL+body+jsonFile key)
  private inFlightRequests = new Map<string, Observable<any>>();
  private readonly readResponseCacheTtlMs = 10000;
  private readResponseCache = new Map<string, { expiresAt: number; value: any }>();

  constructor(
    private http: HttpClient,
    private router: Router,
    private authRefreshService: AuthRefreshService,
    private authTokenStore: AuthTokenStoreService,
    private secureStorage: SecureStorageService,
    private adminAuthService: AdminAuthService
  ) {
    this.company = environment.companyCheck;
    localStorage.setItem("company", this.company);
  }

  refreshToken(): Observable<any> {
    return this.authRefreshService.refreshToken();
  }
  
  
  apiRequest(
    url: string,
    jsonFile: string,
    body: any,
    opts?: {
      cancelable?: boolean;
      forceLive?: boolean;
      responseType?: 'json' | 'blob';
      observe?: 'body' | 'response';
    }
  ): Observable<any> {
    const userId = localStorage.getItem("userID");
    const adminId = localStorage.getItem("adminID");
    if (!userId && !adminId && url.indexOf("auth") < 0 && !url.includes('users/organisation/')) {
      if (!url.includes('/auth/login')) {
        this.router.navigate(['/auth/login'], { queryParamsHandling: 'preserve' });
      }
      return throwError("User not authenticated");
    }
    this.setEnvironment();

    const isAdminContext = this.isAdminContext();

    const requestBody = this.addImpersonationContext(body);
    const normalizedBody = this.normalizeDevicePaymentTypeForApi(url, requestBody);
    const normalizedApiPath = this.normalizeApiPath(url);

    const makeHttpCall = (): Observable<any> => defer(() => {
      const forceLive = opts?.forceLive === true;
      const isDemoEnvironment =
        localStorage.getItem("environment") === "demo" && !forceLive;

      if (!isDemoEnvironment) {
        if (url === "metering/metering_hierarchy/") {
          return this.buildLegacyHierarchyFromNewEndpoints(body?.payload?.userID);
        }
        if (url === "metering/account_hierarchy/") {
          return this.buildLegacyHierarchyFromNewEndpoints(body?.payload?.userID);
        }
      }

      const accessToken$ = isAdminContext
        ? from(this.secureStorage.getItem('admin_access_token'))
        : of(this.authTokenStore.getAccessToken());

      const isFormData = body instanceof FormData;
      const options: any = {
        withCredentials: true,
        responseType: opts?.responseType ?? 'json',
        observe: opts?.observe ?? 'body',
      };

      let userID: any = localStorage.getItem('userID');
      const requestUrl = forceLive
        ? `/api/${normalizedApiPath.replace(/^api\/?/, "")}`
        : this.urlEnvironment + normalizedApiPath;

      return accessToken$.pipe(
        switchMap((accessToken) => {
          if (!isFormData) {
            options.headers = new HttpHeaders({
              "content-type": "application/json",
              ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
            });
          }

          if (userID) {
            const httpRequest = isDemoEnvironment
              ? this.http.get(this.urlEnvironment + url + jsonFile, options)
              : this.http.post(requestUrl, normalizedBody, options);
            return httpRequest;
          }

          return isDemoEnvironment
            ? this.http.get(this.urlEnvironment + url + jsonFile, options)
            : this.http.post(requestUrl, normalizedBody, options);
        })
      );
    });


    // Build a stable key for coalescing (same endpoint + payload)
    const requestKey = this.buildRequestKey(
      `${opts?.forceLive ? "live:" : ""}${url}`,
      jsonFile,
      normalizedBody
    );

    const cacheableReadRequest = !opts?.forceLive
      && (opts?.responseType ?? 'json') === 'json'
      && (opts?.observe ?? 'body') === 'body'
      && this.isCacheableReadRequest(normalizedApiPath);
    const cachedResponse = cacheableReadRequest ? this.getCachedReadResponse(requestKey) : null;
    if (cachedResponse !== null) {
      return of(cachedResponse);
    }

    // Return existing in-flight observable if present
    const existing = this.inFlightRequests.get(requestKey);
    if (existing) {
      return opts?.cancelable !== false ? existing.pipe(takeUntil(this.cancelRequest$)) : existing;
    }

    // Create a shared, cached stream for this request
    const shared$ = makeHttpCall().pipe(
      retryWhen(this.retryStrategy()),
      tap((response) => {
        if (cacheableReadRequest) {
          this.setCachedReadResponse(requestKey, response);
        }
      }),
      finalize(() => {
        // Remove from in-flight map when completed/errored
        this.inFlightRequests.delete(requestKey);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    // Store in-flight shared stream
    this.inFlightRequests.set(requestKey, shared$);

    // Apply per-subscriber cancellation if requested
    const result$ = opts?.cancelable !== false ? shared$.pipe(takeUntil(this.cancelRequest$)) : shared$;

    return result$.pipe(
      catchError(error => {
      // Optional: log or capture the original failed URL
      const failedUrl = error?.url || '';
    
      const refreshAllowedPaths = ['/auth/logout' , '/auth/exit_impersonate_account','/auth/impersonate_account']; 
      if (error.status === 401 && (!failedUrl.includes('/auth/') || refreshAllowedPaths.some(path => failedUrl.includes(path)))) {
        //const accessToken = getAccessToken();
        //const refreshToken = getRefreshToken();

        // If access token and refresh token are available, try to refresh the token
        // if (accessToken && refreshToken) {
        if (!this.isRefreshing) {
          this.isRefreshing = true;
          this.refreshTokenSubject.next(null);
          return this.refreshTokenForContext(isAdminContext).pipe(
            tap((newAccessToken: string) => {
              this.refreshTokenSubject.next(newAccessToken);
            }),
            switchMap(() =>
              makeHttpCall()
            ),
            catchError((refreshError) => {
              const failedRefreshUrl = `${refreshError?.url || ''}`;
              const isRefreshRequestError = failedRefreshUrl.includes('/auth/refresh-token');

              // Unblock queued requests waiting for refresh completion.
              this.refreshTokenSubject.next(this.refreshFailedToken);

              // Logout only when refresh endpoint itself fails (expired/invalid auth session).
              if (isRefreshRequestError) {
                this.router.navigate([isAdminContext ? '/admin/auth/login' : '/auth/login']);
              }
              return this.handleError(refreshError);
            }),
            finalize(() => {
              this.isRefreshing = false;
            })
          );
        } else {
          // Queue the request until refresh completes
          return this.refreshTokenSubject.pipe(
          filter(token => token !== null),
          take(1),
          switchMap((token) => {
            if (token === this.refreshFailedToken) {
              return throwError(() => error);
            }
            return makeHttpCall();
          })
          );
        }
       // }
      }
      // Fallback or if 401 on /auth routes or /auth/logout, don’t try to refresh
      return this.handleError(error);
      })
    );



  }

  private isAdminContext(): boolean {
    return typeof window !== 'undefined'
      && window.location.pathname.startsWith('/admin')
      && !!localStorage.getItem('adminID');
  }

  private refreshTokenForContext(isAdminContext: boolean): Observable<any> {
    return isAdminContext
      ? this.adminAuthService.refreshToken()
      : this.authRefreshService.refreshToken();
  }

  private addImpersonationContext(body: any): any {
    if (!body || typeof body !== 'object' || !body.payload || typeof body.payload !== 'object') {
      return body;
    }

    if (localStorage.getItem('impersination') !== 'yes') {
      return body;
    }

    const impersonatingUserID = Number(
      localStorage.getItem('impersonatingUserID')
    );
    const impersonatedUserID = Number(localStorage.getItem('userID'));
    if (!Number.isInteger(impersonatingUserID)
      || impersonatingUserID <= 0
      || impersonatingUserID === impersonatedUserID
      || Number(body.payload.impersonatingUserID) > 0) {
      return body;
    }

    return {
      ...body,
      payload: {
        ...body.payload,
        impersonatingUserID,
      },
    };
  }

  private normalizeApiPath(url: string): string {
    let normalized = String(url ?? '');
    while (normalized.startsWith('/')) {
      normalized = normalized.slice(1);
    }
    return normalized;
  }

  invalidateRequestCache(url: string, jsonFile: string, body: any, forceLive = false): void {
    const requestKey = this.buildRequestKey(
      `${forceLive ? "live:" : ""}${url}`,
      jsonFile,
      body
    );

    this.readResponseCache.delete(requestKey);
    this.inFlightRequests.delete(requestKey);
  }

  invalidateRequestCacheByUrl(url: string, jsonFile: string, forceLive = false): void {
    const prefix = `${forceLive ? "live:" : ""}${url}|${jsonFile}|`;

    for (const key of Array.from(this.readResponseCache.keys())) {
      if (key.startsWith(prefix)) {
        this.readResponseCache.delete(key);
      }
    }

    for (const key of Array.from(this.inFlightRequests.keys())) {
      if (key.startsWith(prefix)) {
        this.inFlightRequests.delete(key);
      }
    }
  }

  private normalizeDevicePaymentTypeForApi(url: string, body: any): any {
    const isDeviceMutationApi = url === "utilities/add_device/" || url === "utilities/update_device/";
    if (!isDeviceMutationApi || !body || typeof body !== "object" || Array.isArray(body)) {
      return body;
    }

    const payload = (body as any).payload;
    if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
      return body;
    }

    const paymentType = payload.paymentType;
    if (typeof paymentType !== "string") {
      return body;
    }

    const normalized = paymentType.trim().toLowerCase().replace(/[_\s-]+/g, "");
    if (normalized !== "thinprepayment") {
      return body;
    }

    return {
      ...(body as any),
      payload: {
        ...payload,
        paymentType: "thinPrepayment",
      },
    };
  }

  private buildRequestKey(url: string, jsonFile: string, body: any): string {
    if (body instanceof FormData) {
      const entries: string[] = [];
      body.forEach((value, key) => {
        if (value instanceof File) {
          entries.push(`${key}:${value.name}:${value.size}:${value.lastModified}`);
        } else {
          entries.push(`${key}:${value}`);
        }
      });
      const formKey = entries.sort().join('|');
      return `${url}|${jsonFile}|form:${formKey}`;
    }

    const bodyKey = safeStableStringify(body);
    return `${url}|${jsonFile}|${bodyKey}`;
  }
  private setEnvironment(): void {
    this.urlEnvironment = environment.apiUrl;
    // Environment overwrite
    localStorage.setItem("environment", environment.environment ?? "dev");
  }

  private retryStrategy() {
    return (errors: Observable<any>) => {
      return errors.pipe(
        concatMap((error, i) => {
          if (i < this.maxRetries && this.isRetryableError(error)) {
            return of(error).pipe(delay(this.getRetryDelayMs(error, i)));
          }
          return throwError(error);
        })
      );
    };
  }

  private isRetryableError(error: any): boolean {
    return (
      error.status === 429 ||
      error.status === 0 ||
      (error.status !== 0 &&
        error.name === "HttpErrorResponse" &&
        error.message.includes("CORS"))
    );
  }

  private getRetryDelayMs(error: any, attemptIndex: number): number {
    const retryAfterHeader =
      error?.headers?.get?.("Retry-After") ?? error?.headers?.get?.("retry-after");
    const retryAfterSeconds = retryAfterHeader ? parseInt(retryAfterHeader, 10) : NaN;

    if (!Number.isNaN(retryAfterSeconds) && retryAfterSeconds > 0) {
      return Math.min(retryAfterSeconds * 1000, 30000);
    }

    if (error?.status === 429) {
      const baseDelay = Math.min(2000 * Math.pow(2, attemptIndex), 30000);
      return baseDelay + Math.floor(Math.random() * 1000);
    }

    return 2000;
  }

  private isCacheableReadRequest(url: string): boolean {
    const normalizedUrl = this.normalizeApiPath(url);
    const loweredUrl = normalizedUrl.toLowerCase().replace(/\/+$/, '');

    if (
      loweredUrl.endsWith('/integration_logs_paginated') ||
      loweredUrl.endsWith('/integration_run_messages_paginated') ||
      loweredUrl.endsWith('/integration_run_logs_paginated')
    ) {
      return false;
    }

    if (
      loweredUrl.includes("export") ||
      loweredUrl.includes("create") ||
      loweredUrl.includes("update") ||
      loweredUrl.includes("delete") ||
      loweredUrl.includes("save") ||
      loweredUrl.includes("submit") ||
      loweredUrl.includes("import") ||
      loweredUrl.includes("refresh")
    ) {
      return false;
    }

    return (
      /(^|\/)[^\/]+_paginated$/.test(loweredUrl) ||
      /(^|\/)[^\/]+_list_paginated$/.test(loweredUrl) ||
      loweredUrl === "navigation/stats" ||
      loweredUrl === "metering/metering_hierarchy" ||
      loweredUrl === "metering/account_hierarchy" ||
      loweredUrl === "utilities/devices"
    );
  }

  private getCachedReadResponse(requestKey: string): any | null {
    const cached = this.readResponseCache.get(requestKey);
    if (!cached) {
      return null;
    }

    if (cached.expiresAt <= Date.now()) {
      this.readResponseCache.delete(requestKey);
      return null;
    }

    return cached.value;
  }

  private setCachedReadResponse(requestKey: string, value: any): void {
    this.readResponseCache.set(requestKey, {
      expiresAt: Date.now() + this.readResponseCacheTtlMs,
      value,
    });
  }

  private handleError(error: any): Observable<any> {
    
    console.error("API request failed:", error);
    // if(error.error.data){
    //   return throwError(error.error.data[0]);
    // }
    return throwError(error);
  }

  cancelPendingRequests(): void {
    this.cancelRequest$.next();
    this.cancelRequest$.complete(); // Clean up the Subject
    this.cancelRequest$ = new Subject<void>(); // Recreate subject for future requests
  }

  apiRequestDemo(url: string, jsonFile: string, body: any): Observable<any> {
    if (!localStorage.getItem("userID")) {
      this.router.navigateByUrl("/auth/login");
    }
    this.demoUrlEnvironment = "assets/utilitiesdemo/";

    // Get Token from AuthService
    //let accessToken = this.cookieService.get('78Hu78h8:383673:esd838d7s6s7w');
    // const headers = {
    //   "content-type": "application/json",
    //   Authorization: "Bearer " + accessToken,
    // };

    return this.http.get(this.demoUrlEnvironment + url + jsonFile, body);
  }

  apiRequestChatGPT(_url: string, _jsonFile: string, body: any): Observable<any> {
    // The portal edge delegates this same-origin request to the authenticated
    // backend. Provider credentials, authorization, quotas, and request
    // validation must remain behind that authoritative backend boundary.
    return this.apiRequest("openai/chat/completions/", "completions.json", body, {
      forceLive: true,
    });
  }

  private buildLegacyHierarchyFromNewEndpoints(payloadUserId: any): Observable<any> {
    const userID = this.resolveUserId(payloadUserId);
    if (userID === null) {
      return throwError(() => new Error("Unable to resolve user context for hierarchy"));
    }

    const basePayload = { userID, page: 1 };
    return forkJoin({
      farms: this.fetchHierarchyLevel(
        "metering/metering_farms_list_paginated/",
        "metering_farms_list_paginated.json",
        { payload: { ...basePayload, perPage: 50 } },
        ["meteringFarms"]
      ),
      sections: this.fetchHierarchyLevel(
        "metering/metering_sections_list_paginated/",
        "metering_sections_list_paginated.json",
        { payload: { ...basePayload, perPage: 100 } },
        ["meteringSections"]
      ),
      points: this.fetchHierarchyLevel(
        "metering/metering_points_list_paginated/",
        "metering_points_list_paginated.json",
        { payload: { ...basePayload, perPage: 200 } },
        ["meteringPoints"]
      ),
      stats: this.fetchNavigationStats(userID)
    }).pipe(
      map(({ farms, sections, points, stats }) =>
        this.composeHierarchyPayload(stats, farms, sections, points)
      )
    );
  }

  private fetchNavigationStats(userID: number): Observable<any> {
    return this.postHierarchyRequest(
      "metering/navigation/stats",
      "stats.json",
      { payload: { userID } }
    ).pipe(
      map((res) => res?.data ?? res ?? {}),
      catchError(() => of({}))
    );
  }

  private fetchHierarchyLevel(
    url: string,
    jsonFile: string,
    body: Record<string, unknown>,
    keys: string[]
  ): Observable<any[]> {
    return this.postHierarchyRequest(url, jsonFile, body).pipe(
      map((res) => this.extractHierarchyArray(res, keys)),
      catchError(() => of([]))
    );
  }

  private postHierarchyRequest(
    url: string,
    jsonFile: string,
    body: Record<string, unknown>
  ): Observable<any> {
    const normalizedApiPath = this.normalizeApiPath(url);
    const requestKey = this.buildRequestKey(`hierarchy:${normalizedApiPath}`, jsonFile, body);
    const cachedResponse = this.getCachedReadResponse(requestKey);
    if (cachedResponse !== null) {
      return of(cachedResponse);
    }

    const existing = this.inFlightRequests.get(requestKey);
    if (existing) {
      return existing;
    }

    const options: any = { withCredentials: true };
    const accessToken = this.authTokenStore.getAccessToken();
    if (accessToken) {
      options.headers = new HttpHeaders({
        "content-type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      });
    } else {
      options.headers = new HttpHeaders({ "content-type": "application/json" });
    }

    const request$ = localStorage.getItem("environment") === "demo"
      ? this.http.get(this.urlEnvironment + url + jsonFile, options)
      : this.http.post(this.urlEnvironment + normalizedApiPath, body, options);

    const shared$ = request$.pipe(
      retryWhen(this.retryStrategy()),
      tap((response) => {
        this.setCachedReadResponse(requestKey, response);
      }),
      finalize(() => {
        this.inFlightRequests.delete(requestKey);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.inFlightRequests.set(requestKey, shared$);
    return shared$;
  }

  private extractHierarchyArray(
    response: any,
    keys: string[]
  ): any[] {
    const container = response?.data ?? response ?? {};
    for (const key of keys) {
      const candidate = container?.[key];
      if (Array.isArray(candidate)) {
        return candidate;
      }
    }
    if (Array.isArray(container)) {
      return container;
    }
    return [];
  }

  private composeHierarchyPayload(
    stats: any,
    farms: any[],
    sections: any[],
    points: any[]
  ): any {
    const normalizedFarms = farms
      .map((record) => this.normalizeFarm(record))
      .filter((value): value is NormalizedFarm => value !== null);
    const normalizedSections = sections
      .map((record) => this.normalizeSection(record))
      .filter((value): value is NormalizedSection => value !== null);
    const pointsWithMeters = points
      .map((record) => this.normalizePoint(record))
      .filter((value): value is NormalizedPoint => value !== null)
      .map((point) => ({
        ...point,
        meters: this.buildPointMeterPlaceholder(point),
      }));

    const pointsBySection = new Map<number, NormalizedPoint[]>();
    pointsWithMeters.forEach((point) => {
      const sectionId = point.meteringSectionID;
      if (sectionId == null) {
        return;
      }
      const existing = pointsBySection.get(sectionId) ?? [];
      existing.push(point);
      pointsBySection.set(sectionId, existing);
    });

    const sectionsByFarm = new Map<number, NormalizedSection[]>();
    normalizedSections.forEach((section) => {
      const farmId = section.meteringFarmID;
      if (farmId == null) {
        return;
      }
      const sectionWithPoints = {
        ...section,
        meteringPoints: pointsBySection.get(section.meteringSectionID ?? -1) ?? [],
      };
      const existing = sectionsByFarm.get(farmId) ?? [];
      existing.push(sectionWithPoints);
      sectionsByFarm.set(farmId, existing);
    });

    const enrichedFarms = normalizedFarms.map((farm) => ({
      ...farm,
      meteringSections: sectionsByFarm.get(farm.meteringFarmID) ?? [],
    }));

    const sectionsWithPoints = normalizedSections.map((section) => ({
      ...section,
      meteringPoints: pointsBySection.get(section.meteringSectionID ?? -1) ?? [],
    }));

    const responseData: any = {
      navigationStats: stats,
      stats,
    };

    if (enrichedFarms.length > 0) {
      responseData.meteringFarms = enrichedFarms;
    }
    if (sectionsWithPoints.length > 0) {
      responseData.meteringSections = sectionsWithPoints;
    }
    if (pointsWithMeters.length > 0) {
      responseData.meteringPoints = pointsWithMeters;
    }

    return {
      success: true,
      data: responseData,
    };
  }

  private normalizeFarm(record: any): NormalizedFarm | null {
    const id = this.safeParseNumber(
      record?.meteringFarmID ?? record?.meteringFarmId ?? record?.id
    );
    if (id === null) {
      return null;
    }
    return {
      ...record,
      meteringFarmID: id,
      meteringFarmName:
        record?.meteringFarmName ?? record?.name ?? `Farm ${id}`,
    };
  }

  private normalizeSection(record: any): NormalizedSection | null {
    const id = this.safeParseNumber(
      record?.meteringSectionID ?? record?.meteringSectionId ?? record?.id
    );
    if (id === null) {
      return null;
    }
    return {
      ...record,
      meteringSectionID: id,
      meteringSectionName:
        record?.meteringSectionName ?? record?.name ?? `Section ${id}`,
      meteringFarmID:
        this.safeParseNumber(
          record?.meteringFarmID ?? record?.meteringFarmId
        ) ?? undefined,
    };
  }

  private normalizePoint(record: any): NormalizedPoint | null {
    const id = this.safeParseNumber(
      record?.meteringPointID ?? record?.meteringPointId ?? record?.id
    );
    if (id === null) {
      return null;
    }
    return {
      ...record,
      meteringPointID: id,
      meteringPointName:
        record?.meteringPointName ?? record?.name ?? `Point ${id}`,
      meteringSectionID:
        this.safeParseNumber(
          record?.meteringSectionID ??
            record?.meteringSectionId ??
            record?.sectionId
        ) ?? undefined,
      meteringFarmID:
        this.safeParseNumber(
          record?.meteringFarmID ?? record?.meteringFarmId ?? record?.farmId
        ) ?? undefined,
      meterCount:
        this.safeParseNumber(
          record?.meterCount ??
            record?.metersCount ??
            record?.meteringPointCount ??
            record?.meter_count
        ) ?? 0,
    };
  }

  private buildPointMeterPlaceholder(point: NormalizedPoint): any[] {
    return point.meterCount && point.meterCount > 0 ? [{}] : [];
  }

  private resolveUserId(candidate: any): number | null {
    const raw = candidate ?? localStorage.getItem("userID");
    if (raw === null || raw === undefined || raw === "") {
      return null;
    }
    const parsed = parseInt(String(raw), 10);
    return Number.isNaN(parsed) ? null : parsed;
  }

  private safeParseNumber(value: any): number | null {
    if (value === null || value === undefined || value === "") {
      return null;
    }
    const parsed = parseInt(String(value), 10);
    return Number.isNaN(parsed) ? null : parsed;
  }

  getMeteringHierachy(): Observable<any> {
    let body = {
      payload: {
        userID: parseInt(localStorage.getItem("userID") as string),
      },
    };

   

    return this.apiRequest("metering/metering_hierarchy/", "", body).pipe(
      retryWhen((errors) =>
        errors.pipe(
          concatMap((error, i) =>
            i < 5 ? of(error).pipe(delay(1000)) : throwError(error)
          )
        )
      ),
      tap((res: any) => {
        if (res) {
        }
      }),
      catchError((error) => {
        console.error(
          "Failed to retrieve metering hierarchy after retries:",
          error
        );
        return throwError(error);
      })
    );
  }

  getAccountHierachy(): Observable<any> {
    let body = {
      payload: {
        userID: parseInt(localStorage.getItem("userID") as string),
      },
    };

    // if (localStorage.getItem("account_hierarchy")) {
    //   const cachedData = JSON.parse(
    //     localStorage.getItem("account_hierarchy") as string
    //   );
    //   return of(cachedData);
    // }

    return this.apiRequest("metering/account_hierarchy/", "", body).pipe(
      retryWhen((errors) =>
        errors.pipe(
          concatMap((error, i) =>
            i < 5 ? of(error).pipe(delay(1000)) : throwError(error)
          )
        )
      ),
      catchError((error) => {
        console.error(
          "Failed to retrieve account hierarchy after retries:",
          error
        );
        return throwError(error);
      })
    );
  }

  getDevices(): Observable<any> {
    let body = {
      payload: {
        userID: parseInt(localStorage.getItem("userID") as string),
      },
    };
    // if (localStorage.getItem("devices")) {
    //   const cachedData = JSON.parse(localStorage.getItem("devices") as string);
    //   return of(cachedData);
    // }

    return this.apiRequest("utilities/devices/", "", body).pipe(
      retryWhen((errors) =>
        errors.pipe(
          concatMap((error, i) =>
            i < 5 ? of(error).pipe(delay(1000)) : throwError(error)
          )
        )
      ),
      tap((res: any) => {
        if (res) {
        }
      }),
      catchError((error) => {
        console.error("Failed to retrieve devices after retries:", error);
        return throwError(error);
      })
    );
  }
}

interface NormalizedFarm extends Record<string, unknown> {
  meteringFarmID: number;
  meteringFarmName: string;
  meteringSections?: NormalizedSection[];
}

interface NormalizedSection extends Record<string, unknown> {
  meteringSectionID: number;
  meteringSectionName: string;
  meteringFarmID?: number;
  meteringPoints?: NormalizedPoint[];
}

interface NormalizedPoint extends Record<string, unknown> {
  meteringPointID: number;
  meteringPointName: string;
  meteringSectionID?: number;
  meteringFarmID?: number;
  meterCount: number;
  meters?: any[];
}
